// ── S2 — RENDER: the grouped view as Atlas's pick-list ────────────────────────────────────────────────
// PURE: no Angular, no I/O — unit-testable against a cached grouped view, same as the hydrator.
//
// This is the OTHER rendering. `buildAtlasDynamicContext` (olympus-functions) renders what is LEFT after
// selection, for Forge to write steps from. This renders everything there is TO select from, for Atlas.
// They are not variants of one function: Forge needs slots it can fill, Atlas needs evidence it can judge.
//
// WHAT ATLAS EMITS: `{ id, why }[]` and nothing else. So every line here leads with the id, and every
// other fact exists to help CHOOSE — never to be copied into a step. Atlas cannot hallucinate a token, a
// type or an owner because it never states one; the hydrator looks all of that up afterwards.
//
// COLUMN SELECTION: TRIED, REVERTED, RE-APPLIED — all on 2026-08-09. Atlas picks filter/sort columns and
// the payload carries only those. The round trip is worth keeping because the reversal was correct at the
// time and the re-application depends on a change made in between:
//
//   · REVERTED because a column Atlas did not pick is ABSENT, and Forge asked for an absent column only
//     ~70% of the time, silently dropping the filter or sort otherwise (V22, controlled A/B, 16 runs).
//     A dropped WHERE returns every row; a dropped ORDER BY returns them unordered; neither errors.
//   · RE-APPLIED once that ask became reliable. The plan prompt had made producing the job and refusing a
//     footnote, with a "add a WARNING line" escape that complied with both. Rebalanced, the same A/B went
//     to 16 of 16 asked, 0 dropped.
//   · accuracy was never the axis: over fourteen runs, at 49 columns and at 1, Forge chose V08's dotted
//     reference column correctly every time. Narrowing buys tokens, not correctness.
//
// THE REFERENCE LINK IS NOT A COLUMN and is rendered independently of all this — `linkId` resolves to an
// ENTITYREFERENCEFORCRUD variable used in `entityReferences[].field`. Nothing else offers it.
//
// DELIBERATELY ABSENT
//   · the raw flat-map token — the whole point of ids is that a real variable name never reaches a model.
//
// DELIBERATELY PRESENT
//   · `usage`, rendered in full rather than counted. "Three functions write this" is the difference
//     between a strong candidate and a weak one, and ABSENCE IS THE SIGNAL — a variable nothing has ever
//     touched says so by having no usage line, which is why nothing is emitted rather than "(unused)".
//     ⚠ ABSENCE POINTS BOTH WAYS, and the prompt is where that is resolved (atlas.prompts.ts, "Two kinds
//     of pick"). For an INTENT pick — something the request named — no usage means weak. For a CAPABILITY
//     pick — an accumulator, a loop counter, a row index, none of which any request mentions — an idle
//     variable is exactly RIGHT, and one already carrying something is a collision. Same rendering, two
//     readings, which is why this file states the evidence and never scores it.
//   · both descriptions, because they answer different questions and conflating them loses one:
//       field description  = what the DATA means      "the lead's full name"
//       variant meaning    = why this HOLDER exists   "the lead currently open in the editor"

import { GroupedView, GroupedEntity, GroupedField, GroupedVariant, EntityHolder } from './atlas-grouped-view.util';

export interface PickListOpts {
  /** Page functions available to call. Not variables, so they never enter the map — passed in. */
  functions?: { name: string; description?: string }[];
  /** Workflows available to call. Same reason. */
  workflows?: { name: string; description?: string }[];
}

/** `usage` → one compact line. Returns '' when nothing touches it — absence is the signal, not "(unused)". */
function usageLine(u: GroupedVariant['usage']): string {
  if (!u) return '';
  // ONE LINE, whatever the description holds: a description written as pseudo-code spans lines, and a break
  // left in it pushed the rest of the uses onto lines of their own, outside the variable they belong to.
  const oneLine = (s: string | undefined) => (s || '').replace(/\s+/g, ' ').trim();
  const bits: string[] = [];
  if (u.functions?.length) {
    // The DESCRIPTION carries the evidence, not the name. Half the estate's functions are called
    // `system_fn_flagChange_5ed8` or `defaultFunction`, and "three functions write this" is a far weaker
    // signal than "the function that saves the transaction writes this". Rendered when present, and
    // nothing is emitted when absent — absence is the signal here as everywhere else in this file.
    bits.push(u.functions.map((f) => {
      const d = oneLine(f.description);
      return `${f.name}:${f.access === 'both' ? 'rw' : f.access[0]}${d ? ` "${d.slice(0, 72)}"` : ''}`;
    }).join(', '));
  }
  if (u.components?.length) {
    // The binding's own description is the clearest statement of intent anywhere in the system — it says
    // why a human wired this component to this variable, which is exactly what Atlas is trying to infer.
    bits.push(u.components.map((c) => {
      const d = oneLine(c.description);
      return `${c.componentName}.${c.property}${c.direction === 'output' ? '→' : '←'}${d ? ` "${d.slice(0, 48)}"` : ''}`;
    }).join(', '));
  }
  if (u.sections?.length) {
    bits.push(u.sections.map((s) => `${s.name}[${s.binding}]`).join(', '));
  }
  return bits.length ? `used by ${bits.join(' · ')}` : '';
}

function variantLine(v: GroupedVariant, indent: string, holderMeanings?: Set<string>): string[] {
  const out: string[] = [];
  const via = v.via ? ` via ${v.via}` : '';
  // THE AOB LANE SAYS WHAT IT COSTS TO WRITE (2026-08-11).
  //
  // The cursor and the fields written through it both rendered as the same three words — `AOB reference` —
  // so nothing distinguished "this positions a row" from "this is a value on that row", and nothing said
  // that writing the value REQUIRES the cursor and the list. Atlas read a field as directly writable and
  // picked it alone: benchmark ATLAS-a1..a4 missed the cursor in V16 (3 of 4 passes), V01 ($D63, 4 of 4)
  // and V02 ($N31), which leaves the step generator unable to position anything.
  //
  // Forge's payload was given the same sentence a day earlier, for the identical misconception on the
  // other side of the seam, and it moved that case from 1/5 to 5/5. This is the mirror of it: the input
  // states what the consumer requires, rather than leaving each agent to deduce it.
  const aobNote = v.lane === 'AOB reference'
    ? (v.type === 'REFERENCE_ENTITY_VARIABLE_AOB'
        ? '  ← the CURSOR for this nested list: it selects WHICH row. Writing any field of a row needs it, plus the list and a row number.'
        : '  ← a field of a nested row, written THROUGH the cursor above. Picking it alone is not enough: the cursor and the list are separate picks.')
    : '';
  out.push(`${indent}${v.id}  ${v.type} · ${v.lane}${via}${aobNote}`);
  // STATE THE HOLDER'S MEANING ONCE. `GroupedVariant.meaning` is the description of the VARIABLE this
  // handle is rooted in, so every field reached through `editOotbLeadsEntity` carries that holder's
  // sentence — and the holder is already listed under `held by:` with it. Repeating it per field turned
  // one real page into 53,768 characters of mostly the same sentence. `via <holder>` is the reference;
  // the text belongs where the holder is introduced.
  if (v.meaning && !(holderMeanings && holderMeanings.has(v.meaning))) out.push(`${indent}    ${v.meaning}`);
  const u = usageLine(v.usage);
  if (u) out.push(`${indent}    ${u}`);
  return out;
}

/**
 * SCOPE IS ON THE LINE (2026-08-11). It was in `EntityHolder.scope` all along and thrown away here, which
 * made every entity holder render identically:
 *
 *     $2   BudgetEnity        (entity, ENTITY_VARIABLE)      ← global
 *     $D5  localBudgetEntity  (entity, ENTITY_VARIABLE)      ← local
 *
 * Standalone variables have always carried it (`Number · local var`), so the gap was only here — and it is
 * the one fact that decides a whole class of request. "Keep the finished budget for the rest of the
 * session" needs a global holder, and V01 could not tell which of those two is one. Measured: one pass
 * concluded "no existing global variable" and asked to CREATE a permanent global that already existed;
 * another rejected `$D5` on the grounds that it is local, which it worked out from the NAME — the single
 * inference the system prompt warns hardest against ("the one whose NAME matches is routinely the wrong
 * one"), and it only worked because this variable happens to be called `localBudgetEntity`.
 *
 * A create is permanent, so the cost of withholding this is not a wrong pick — it is a duplicate variable
 * in every future pick list for the life of the project.
 */
function holderLine(h: EntityHolder, indent: string): string[] {
  const out: string[] = [`${indent}${h.id}  ${h.name}  (${h.kind}, ${h.type})${h.scope ? ` · ${h.scope}` : ''}`];
  if (h.meaning) out.push(`${indent}    ${h.meaning}`);
  const u = usageLine(h.usage);
  if (u) out.push(`${indent}    ${u}`);
  return out;
}

/**
 * The other two handles a field can carry, rendered so Atlas can pick them (2026-08-08).
 *
 * ONE FIELD, THREE JOBS, THREE DIFFERENT ID SPACES — and they are not interchangeable:
 *
 *   variants[].id   $B3      READ its value      → "var" / "target"
 *   columnId        $Q1_7    FILTER or SORT on it → "conditions[].field", "orderBy[]", aggregate columns
 *   linkId          $A6      LINK it on a write  → "entityReferences[].field"
 *
 * They were withheld from this list until now, which is why the payload had to ship EVERY column of every
 * picked entity — the `AtlasContextQueryField` contract has always claimed the payload carries "ONLY
 * ATLAS'S PICKS", and that was untrue for as long as Atlas could not name one.
 */
function handleLine(f: GroupedField, indent: string): string[] {
  const out: string[] = [];
  // COLUMN IDS ARE RENDERED — removed 2026-08-09 and restored the same day once the ask became reliable.
  // See atlas-hydrate.util.ts `collectColumns` for the round trip and the numbers.
  if (f.columnId) {
    const kind = f.aaob ? 'AAOB column — needs a runtime label to filter or sort' : 'filter/sort column';
    out.push(`${indent}${f.columnId}  ${kind}${f.columnType ? ` · ${f.columnType}` : ''}`);
  }
  // The reference link is a DIFFERENT lane, not a column: `linkId` resolves to an ENTITYREFERENCEFORCRUD
  // variable named in `entityReferences[].field` to ATTACH a relationship on a write.
  if (f.linkId) out.push(`${indent}${f.linkId}  reference link — to attach this relationship when writing a record`);
  // What already writes the link, stated as for any other handle; nothing when nothing does.
  const u = f.linkId ? usageLine(f.linkUsage) : '';
  if (u) out.push(`${indent}    ${u}`);
  return out;
}

/**
 * WHICH ENTITY A REFERENCE POINTS AT (2026-08-11). `refEntity` was populated on all 166 reference fields
 * across the benchmark's pages and rendered on none of them.
 *
 * Its own contract says why that matters: `type: 'ENTITY_REFERENCE'` cannot express the target, and the
 * pattern it makes possible — fetching the children of a parent — compares the column against an ENTITY
 * VARIABLE, so which entity that variable must hold is the entire question:
 *     {"field":"leadRefference","op":"_eq","value":{"var":"globalVariable$editOotbLeadsEntity"}}
 * Without it those sites are, in the field's own words, inexpressible: the agent can see the column, can
 * pick it, and has no way to know which of the page's entity variables is legal on the other side.
 *
 * The NAME is printed, not the id — the target is frequently an entity this page does not import, so it
 * has no handle here, and printing a handle-shaped string for something unpickable would invite a pick.
 *
 * ⚠ STATED AS A FACT, NEVER AS A REQUIREMENT — this line was first written as "comparing this column needs
 * a variable holding a record of THAT entity", and the word `needs` cost V01 immediately. Its Budget has a
 * `userRef` the request never mentions; the agent read the obligation, checked whether it had a Person
 * variable, found none because this page does not import Person, and spent a third of its reasoning
 * drafting a warning about a relationship nobody asked for — in one pass ending on "No handle is available
 * to link the budget to the current user". Rewording it to a plain statement took V01's extras from 2.67
 * per pass to 0.33 with everything else held. (It did NOT reduce reasoning volume, which was the first
 * explanation offered and the wrong one: the mean rose slightly. What changed is what the agent did with
 * the line, not how long it thought about it.)
 *
 * The information is worth having and the instruction was not. A line that says what something IS gets read
 * when it is relevant; a line that says what something NEEDS gets satisfied whether or not anyone asked.
 */
function refTargetLine(f: GroupedField, indent: string): string[] {
  return f.refEntity ? [`${indent}↳ holds a reference to ${f.refEntity}`] : [];
}

function fieldLines(f: GroupedField, indent: string, path: string[], holderMeanings?: Set<string>): string[] {
  const out: string[] = [];
  const here = [...path, f.field];
  // A node with no variants is not necessarily a ROUTE. Three cases, and conflating them lost a whole lane:
  //   · variants          → readable, and each variant is a way to read it
  //   · columnId / linkId → NOT readable, still pickable — a column inside an AOB hanging off a reference
  //                         has no Blockly variable, yet is perfectly legal to filter on and the human
  //                         picker offers it. Before 2026-08-08 this function returned early on those and
  //                         they never appeared at all.
  //   · neither, but children → a pure hop like `metaFormRef`; a heading, nothing to pick
  const handles = handleLine(f, indent + '  ');
  const pickable = (f.variants || []).length > 0 || handles.length > 0;
  const kids = (f.fields || []).flatMap((sub) => fieldLines(sub, indent + '  ', here, holderMeanings));
  if (!pickable && !kids.length) return out;
  // The reference target is stated in BOTH branches on purpose. A reference is sometimes pickable (it has a
  // filterable column) and sometimes only a hop to what hangs off it — and in both cases the question "which
  // entity does this point at" has the same answer and the same consequence for anything comparing it.
  if (pickable) {
    out.push(`${indent}${here.join('.')}${f.description ? ` — ${f.description}` : ''}`);
    out.push(...refTargetLine(f, indent + '  '));
    for (const v of f.variants) out.push(...variantLine(v, indent + '  ', holderMeanings));
    out.push(...handles);
  } else if (kids.length) {
    out.push(`${indent}${here.join('.')}${f.description ? ` — ${f.description}` : ''}   (route)`);
    out.push(...refTargetLine(f, indent + '  '));
  }
  out.push(...kids);
  return out;
}

/**
 * A LOCAL ENTITY HAS NO TABLE, AND UNTIL NOW NOTHING SAID SO (2026-08-11).
 *
 * `GroupedEntity.status` is populated on every entity and was rendered nowhere: 79 entities carried one
 * across the benchmark's pages, 0 reached the agent. 11 of those 79 — 14% — are `LOCAL`, meaning they were
 * created in the editor and never synced, so there is no database table behind them. Querying, counting or
 * creating one fails at runtime with "quering the LOCAL Entity which is not possible", and the type's own
 * comment says this field exists for exactly that check.
 *
 * The system prompt has been telling the agent to make that check — "a table marked LOCAL cannot be
 * queried, counted or created; say so in warnings rather than picking it" — against information it was
 * never given. A rule that points at a fact the payload withholds is worse than no rule: it reads as
 * covered.
 *
 * Only the abnormal statuses are stated. SYNCED is the overwhelming majority (68 of 79) and is what a
 * reader already assumes, so printing it would spend a line per entity to say "normal".
 */
const ENTITY_STATUS_NOTE: Record<string, string> = {
  LOCAL: '   ⚠ LOCAL — never synced, so there is NO database table. It cannot be queried, counted, aggregated or created. Say so in `warnings` instead of picking it.',
  NEW: '   ⚠ NEW — not yet synced to a database table. Treat as unavailable for queries and creates.',
  READY: '   ⚠ READY — schema saved but not synced yet; its table may not exist.',
};

function entityBlock(e: GroupedEntity): string[] {
  const out: string[] = [];
  out.push(`${e.id || '(no handle)'}  ${e.name}${e.description ? ` — ${e.description}` : ''}`);
  const statusNote = e.status ? ENTITY_STATUS_NOTE[e.status] : undefined;
  if (statusNote) out.push(statusNote);
  if (e.heldBy?.length) {
    out.push('  held by:');
    for (const h of e.heldBy) out.push(...holderLine(h, '    '));
  }
  // Every meaning already printed under `held by:` — so a field variant does not repeat it.
  const holderMeanings = new Set((e.heldBy || []).map((h) => h.meaning).filter(Boolean) as string[]);
  const fields = (e.fields || []).flatMap((f) => fieldLines(f, '    ', [], holderMeanings));
  if (fields.length) { out.push('  fields:'); out.push(...fields); }
  return out;
}

/**
 * Render the grouped view as the list Atlas selects from.
 *
 * Ordering is by SECTION, not by score: entities first (most functions operate on one), then standalone
 * page/session variables, then the flat groups. Atlas is asked to weigh `usage`, so pre-sorting by it
 * here would be this function making the judgement instead of stating the evidence.
 */
export function renderAtlasPickList(view: GroupedView, opts?: PickListOpts): string {
  const parts: string[] = [];

  if (view.entities?.length) {
    parts.push('## Entities — and the variables that carry their records\n');
    for (const e of view.entities) { parts.push(...entityBlock(e)); parts.push(''); }
  }

  const standalone = [...(view.localVars || []), ...(view.globalVars || [])];
  if (standalone.length) {
    parts.push('## Page and session variables\n');
    for (const v of standalone) {
      const name = v.name ? `  ${v.name}` : '';
      parts.push(`${v.id}${name}  ${v.type} · ${v.lane}`);
      if (v.meaning) parts.push(`    ${v.meaning}`);
      const u = usageLine(v.usage);
      if (u) parts.push(`    ${u}`);
    }
    parts.push('');
  }

  for (const g of view.standalone || []) {
    if (!g.items?.length) continue;
    parts.push(`## ${g.group}\n`);
    for (const it of g.items) {
      parts.push(`${it.id}  ${it.token}  ${it.type}${it.description ? ` — ${it.description}` : ''}`);
      const u = usageLine(it.usage);
      if (u) parts.push(`    ${u}`);
    }
    parts.push('');
  }

  // Functions and workflows are NOT variables and never enter the map, so they arrive separately. Atlas
  // still needs them: "call the existing recalculateTotals" is a selection, and without this it cannot
  // know that function exists.
  if (opts?.functions?.length) {
    parts.push('## Existing page functions\n');
    for (const f of opts.functions) parts.push(`${f.name}${f.description ? ` — ${f.description}` : ''}`);
    parts.push('');
  }
  if (opts?.workflows?.length) {
    parts.push('## Workflows\n');
    for (const w of opts.workflows) parts.push(`${w.name}${w.description ? ` — ${w.description}` : ''}`);
    parts.push('');
  }

  // Capability gaps are stated, never inferred from a gap. A lane the agent cannot see is a capability it
  // cannot use, and silence there reads as "there is nothing", which is a different claim.
  const s = view.stats;
  const notes: string[] = [];
  if (s?.droppedLane) notes.push(`${s.droppedLane} handle(s) withheld by lane routing (${(s.droppedLanes || []).join(', ')})`);
  if (s?.droppedUnimported) notes.push(`${s.droppedUnimported} global entity variable(s) whose entity this page does not import`);
  if (s?.orphans) notes.push(`${s.orphans} stale token(s) that match no field`);
  if (notes.length) parts.push('## Not shown\n' + notes.map((n) => `- ${n}`).join('\n') + '\n');

  return parts.join('\n');
}
